import { Box, styled, Typography } from '@mui/material';
import { EColors, getColor } from 'theme';
import { LabeledTextField } from './LabeledTextField';
import { SelectField } from './SelectField';

const LOW_VALUE_OPTIONS = [
    { value: 0, label: '0' },
    { value: 1, label: '1' },
];

const HIGH_VALUE_OPTIONS = Array.from({ length: 8 }, (_, i) => ({
    value: i + 3,
    label: String(i + 3),
}));

const Container = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
});

const ScaleRow = styled(Box)(({ theme: { spacing } }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing(1.5),
}));

const LabelGroup = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    flex: 1,
});

const ValueGroup = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    width: '100px',
});

const GroupLabel = styled(Typography)({
    fontSize: '0.75rem',
    fontWeight: 500,
    color: getColor(EColors.pureBlack),
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
});

const RequiredLabel = styled('span')({
    fontSize: '0.75rem',
    fontWeight: 400,
    color: getColor(EColors.pureBlack, 0.5),
});

const Separator = styled(Typography)({
    fontSize: '0.875rem',
    color: getColor(EColors.v3TextSecondary),
    alignSelf: 'center',
    marginTop: '1.75rem',
});

type LinearScaleOptions = {
    lowLabel?: string;
    lowValue?: number;
    highLabel?: string;
    highValue?: number;
};

type LinearScalePromptProps = {
    label?: string;
    options?: LinearScaleOptions;
    disabled?: boolean;
    /** Locks just the scale bounds/labels, leaving the question label on `disabled`. */
    optionsDisabled?: boolean;
    onLabelChange?: (newLabel: string) => void;
    onOptionsChange?: (newOptions: LinearScaleOptions) => void;
};

export const LinearScalePrompt = ({
    label = '',
    options = {},
    disabled = false,
    optionsDisabled = false,
    onLabelChange,
    onOptionsChange,
}: LinearScalePromptProps) => {
    const { lowLabel = '', lowValue = 1, highLabel = '', highValue = 5 } = options;

    const handleOptionChange = <K extends keyof LinearScaleOptions>(field: K, value: LinearScaleOptions[K]) => {
        onOptionsChange?.({
            ...options,
            [field]: value,
        });
    };

    return (
        <Container>
            <LabeledTextField
                label="Question"
                placeholder="Enter question text"
                value={label}
                onChange={e => onLabelChange?.(e.target.value)}
                disabled={disabled}
            />

            <ScaleRow>
                <LabelGroup>
                    <GroupLabel>Label</GroupLabel>
                    <LabeledTextField
                        placeholder="Label Text"
                        value={lowLabel}
                        onChange={e => handleOptionChange('lowLabel', e.target.value)}
                        disabled={optionsDisabled}
                    />
                </LabelGroup>

                <ValueGroup>
                    <GroupLabel>
                        Low <RequiredLabel>(required)</RequiredLabel>
                    </GroupLabel>
                    <SelectField
                        value={lowValue}
                        options={LOW_VALUE_OPTIONS}
                        onChange={value => handleOptionChange('lowValue', value as number)}
                        disabled={optionsDisabled}
                    />
                </ValueGroup>

                <Separator>to</Separator>

                <LabelGroup>
                    <GroupLabel>Label</GroupLabel>
                    <LabeledTextField
                        placeholder="Label Text"
                        value={highLabel}
                        onChange={e => handleOptionChange('highLabel', e.target.value)}
                        disabled={optionsDisabled}
                    />
                </LabelGroup>

                <ValueGroup>
                    <GroupLabel>
                        High <RequiredLabel>(required)</RequiredLabel>
                    </GroupLabel>
                    <SelectField
                        value={highValue}
                        options={HIGH_VALUE_OPTIONS}
                        onChange={value => handleOptionChange('highValue', value as number)}
                        disabled={optionsDisabled}
                    />
                </ValueGroup>
            </ScaleRow>
        </Container>
    );
};
